import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, Select, Switch } from "@sagot/ui";
import { Controller, useFormContext } from "react-hook-form";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { AgentStatus } from "../shared/types";
import { LANGUAGES, RETENTION_OPTIONS, type SettingsValues } from "./settings-model";
import { SettingsCard, SwitchRow } from "./settings-ui";

export function LanguagesTab({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { control } = useFormContext<SettingsValues>();
  return (
    <>
      <SettingsCard title={t("build.agentSettings.languages.title")} hint={t("build.agentSettings.languages.hint")} flush>
        <Controller
          control={control}
          name="languages"
          render={({ field }) => (
            <ul className="divide-y divide-line">
              {LANGUAGES.map((code) => {
                const on = field.value.includes(code);
                const last = on && field.value.length === 1;
                const name = t(`build.agentSettings.languages.names.${code}`);
                return (
                  <li key={code} className="flex items-center justify-between gap-4 px-5 py-4">
                    <div className="flex items-center gap-3">
                      <span aria-hidden="true" className="inline-flex size-8 items-center justify-center rounded-md bg-sunken text-xs font-bold">{t(`build.agentSettings.languages.codes.${code}`)}</span>
                      <div>
                        <div className="text-sm font-semibold">
                          {name}
                          {code === "pam" ? <Badge tone="warning" className="ml-1">{t("build.agentSettings.languages.beta")}</Badge> : null}
                        </div>
                        <div className="text-xs text-fg-subtle">{last ? t("build.agentSettings.languages.required") : t(`build.agentSettings.languages.descriptions.${code}`)}</div>
                      </div>
                    </div>
                    <Switch
                      aria-label={name}
                      checked={on}
                      disabled={disabled || last}
                      onCheckedChange={(next) => { field.onChange(next ? [...field.value, code] : field.value.filter((l) => l !== code)); }}
                    />
                  </li>
                );
              })}
            </ul>
          )}
        />
      </SettingsCard>
      <section className="card">
        <div className="card-body space-y-4">
          <SwitchRow name="reply_in_user_language" label={t("build.agentSettings.languages.reply")} hint={t("build.agentSettings.languages.replyHint")} disabled={disabled} className="" />
          <div className="flex items-start gap-3 rounded-md bg-surface-2 p-3.5 text-xs text-fg-muted">
            <Icon name="languages" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
            <p><strong className="text-fg">{t("build.agentSettings.languages.noTranslate")}</strong> {t("build.agentSettings.languages.noTranslateBody")}</p>
          </div>
        </div>
      </section>
    </>
  );
}

export function MemoryTab({ disabled }: { disabled: boolean }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const form = useFormContext<SettingsValues>();
  return (
    <SettingsCard title={t("build.agentSettings.memory.title")} hint={t("build.agentSettings.memory.hint")} flush>
      <div className="divide-y divide-line">
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <label htmlFor="agent-retention" className="text-sm font-semibold">{t("build.agentSettings.memory.keep")}</label>
            <p className="mt-0.5 text-xs text-fg-subtle">{t("build.agentSettings.memory.keepHint")}</p>
          </div>
          <Select id="agent-retention" className="sm:w-48" disabled={disabled} {...form.register("retention")}>
            <option value="">{t("build.agentSettings.memory.workspaceDefault")}</option>
            {RETENTION_OPTIONS.map((days) => <option key={days} value={days}>{t("build.agentSettings.memory.days", { count: Number(days) })}</option>)}
          </Select>
        </div>
        <SwitchRow name="remember_users" label={t("build.agentSettings.memory.remember")} hint={t("build.agentSettings.memory.rememberHint")} disabled={disabled} />
        <SwitchRow name="anonymize_after_retention" label={t("build.agentSettings.memory.anonymize")} hint={t("build.agentSettings.memory.anonymizeHint")} disabled={disabled} />
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold">{t("build.agentSettings.memory.dsr")}</div>
            <p className="mt-0.5 text-xs text-fg-subtle">{t("build.agentSettings.memory.dsrHint")}</p>
          </div>
          <Button asChild variant="secondary" size="sm">
            <AppLink href={consoleRoutes.settings(summary.slug, "privacy")}>
              <Icon name="user" />
              {t("build.agentSettings.memory.dsrOpen")}
            </AppLink>
          </Button>
        </div>
      </div>
    </SettingsCard>
  );
}

interface DangerTabProps {
  status: AgentStatus;
  canToggle: boolean;
  canDelete: boolean;
  toggling: boolean;
  onToggle: () => void;
  onDelete: () => void;
}

export function DangerTab({ status, canToggle, canDelete, toggling, onToggle, onDelete }: DangerTabProps) {
  const { t } = useTranslation();
  const paused = status !== "live";
  return (
    <section className="card border-danger/40" aria-labelledby="danger-title">
      <div className="card-header"><h2 id="danger-title" className="card-title text-danger">{t("build.agentSettings.danger.title")}</h2></div>
      <div className="divide-y divide-line">
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold">{t(paused ? "build.agentSettings.danger.resume" : "build.agentSettings.danger.pause")}</div>
            <p className="mt-0.5 text-xs text-fg-subtle">{t("build.agentSettings.danger.pauseHint")}</p>
          </div>
          <Button variant="secondary" disabled={!canToggle} loading={toggling} onClick={onToggle}>
            <Icon name={paused ? "play" : "pause"} />
            {t(paused ? "build.agentSettings.danger.resume" : "build.agentSettings.danger.pause")}
          </Button>
        </div>
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold">{t("build.agentSettings.danger.delete")}</div>
            <p className="mt-0.5 text-xs text-fg-subtle">{t("build.agentSettings.danger.deleteHint")}</p>
          </div>
          <Button variant="danger" disabled={!canDelete} onClick={onDelete}>
            <Icon name="trash" />
            {t("build.agentSettings.danger.delete")}
          </Button>
        </div>
      </div>
    </section>
  );
}
